<template>
  <v-btn
    :class="{
      loading: loading,
      'default-button': applyDefault,
      'outlined-button': applyOutlined,
      small: applySmall,
      compact: applyDensity,
      block: applyBlock
    }"
    :loading="loading"
    rounded="pill"
    :base-color="buttonColor"
    :variant="variant"
  >
    <template v-if="$slots.append" #append>
      <slot name="append"></slot>
    </template>
    <template #loader>
      <span class="loader"> </span>
    </template>
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend"></slot>
    </template>
  </v-btn>
</template>
<script>
export default {
  name: "DefaultButton",
  props: {
    variant: {
      type: String,
      default: "flat",
      validator: (value) => ["flat", "outlined"].includes(value)
    },
    density: {
      type: String,
      default: "default",
      validator: (value) => ["default", "compact"].includes(value)
    },
    size: {
      type: String,
      default: "default",
      validator: (value) => ["default", "small"].includes(value)
    },
    typeColor: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "secondary"].includes(value)
    },
    loading: { type: Boolean, default: false },
    block: { type: Boolean, default: false }
  },
  data: () => ({}),
  computed: {
    applyDefault() {
      return this.variant === "flat";
    },
    applyOutlined() {
      return this.variant === "outlined";
    },
    applySmall() {
      return this.size === "small";
    },
    applyDensity() {
      return this.density === "compact";
    },
    applyBlock() {
      return this.block === true;
    },
    buttonColor() {
      if (this.typeColor === "primary") {
        return this.colors.primary;
      } else {
        return this.colors.secondary;
      }
    },
    hoverColor() {
      return this.colors.hover;
    },
    focusColor() {
      return this.colors.focus;
    },
    loadingColor() {
      return `linear-gradient(63.59deg, ${this.colors.primary} -5.12%, ${this.colors.gradient} 102.9%)`;
    },
    colors() {
      return this.$vuetify.theme.global.current.colors;
    }
  }
};
</script>
<style scoped>
/* CSS para versão Desktop */
@media (min-width: 1200px) and (orientation: landscape) {
  .default-button {
    font-size: 0.9vw;
    font-weight: 500;
    text-transform: none;
    line-height: 1vw;
    color: #ffffff !important;
    letter-spacing: 0vw;
    height: 3vw;
  }
  .outlined-button {
    font-size: 0.9vw;
    font-weight: 500;
    text-transform: none;
    line-height: 1vw;
    color: #102645 !important;
    letter-spacing: 0vw;
    height: 3vw;
  }
  .compact {
    max-height: 1.5vw !important;
  }
  .small {
    max-width: 6vw !important;
  }
  .loader {
    width: 1vw;
    height: 1vw;
    border: 0.15vw solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }
}

/* CSS para versão Tablet na horizontal */
@media (max-width: 1200px) and (min-height: 600px) and (orientation: landscape) {
  .default-button {
    font-size: 1.2vw;
    font-weight: 500;
    text-transform: none;
    line-height: 1.3vw;
    color: #ffffff !important;
    letter-spacing: 0vw;
    height: 3vw;
  }
  .outlined-button {
    font-size: 1.2vw;
    font-weight: 500;
    text-transform: none;
    line-height: 1.3vw;
    color: #102645 !important;
    letter-spacing: 0vw;
    height: 3vw;
  }
  .compact {
    max-height: 1.5vw !important;
  }
  .small {
    max-width: 7vw !important;
  }
  .loader {
    width: 1.5vw;
    height: 1.5vw;
    border: 0.15vw solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }
}

/* CSS para versão Tablet na vertical */
@media (min-width: 600px) and (orientation: portrait) {
  .default-button {
    font-size: 1.5vh;
    font-weight: 500;
    text-transform: none;
    line-height: 1.3vh;
    color: #ffffff !important;
    letter-spacing: 0vh;
    height: 4.5vh;
  }
  .outlined-button {
    font-size: 1.2vh;
    font-weight: 500;
    text-transform: none;
    line-height: 1.3vh;
    color: #102645 !important;
    letter-spacing: 0vh;
    height: 4.5vh;
  }
  .compact {
    max-height: 2vh !important;
  }
  .small {
    max-width: 8vh !important;
  }
  .loader {
    width: 1.5vh;
    height: 1.5vh;
    border: 0.25vh solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }
}

/* CSS para versão Mobile na horizontal */
@media (max-width: 1024px) and (max-height: 600px) and (orientation: landscape) {
  .default-button {
    font-size: 2.1vw;
    font-weight: 500;
    text-transform: none;
    line-height: 2.2vw;
    color: #ffffff !important;
    letter-spacing: 0vw;
    height: 6vw;
  }
  .outlined-button {
    font-size: 2.1vw;
    font-weight: 500;
    text-transform: none;
    line-height: 2.2vw;
    color: #102645 !important;
    letter-spacing: 0vw;
    height: 6vw;
  }
  .compact {
    max-height: 3.5vw !important;
  }
  .small {
    min-width: 5vw !important;
  }
  .loader {
    width: 2.5vw;
    height: 2.5vw;
    border: 0.35vw solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }
}

/* CSS para versão Mobile na vertical */
@media (max-width: 600px) and (orientation: portrait) {
  .default-button {
    font-size: 2.1vh;
    font-weight: 500;
    text-transform: none;
    line-height: 2.2vh;
    color: #ffffff !important;
    letter-spacing: 0vh;
    height: 6vh;
  }
  .outlined-button {
    font-size: 2.1vh;
    font-weight: 500;
    text-transform: none;
    line-height: 2.2vh;
    color: #102645 !important;
    letter-spacing: 0vh;
    height: 6vh;
  }
  .compact {
    max-height: 3.5vw !important;
  }
  .small {
    min-width: 5vw !important;
  }
  .loader {
    width: 2.5vh;
    height: 2.5vh;
    border: 0.35vh solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }
}

.default-button:focus {
  background-color: v-bind(focusColor) !important;
}
.default-button:hover {
  background-color: v-bind(hoverColor) !important;
}
.loading {
  background: v-bind(loadingColor);
}
.block {
  width: 100%;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
